import { useEffect, useRef, useState } from 'react';

import { LicensesSection, LicensesSectionHandle } from 'pages/partner_create_edit/components/licenses_section/LicensesSection';
import { StagedUser, UsersSection, UsersSectionHandle } from 'pages/partner_create_edit/components/users_section/UsersSection';
import { PartnerDetailsSection } from 'pages/partner_create_edit/components/partner_details_section/PartnerDetailsSection';
import { PartnerApi, PartnerDetails, PartnerTypes } from 'apis/PartnerApi';
import { usePartnerCreateEdit } from 'pages/partner_create_edit/context';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { helpContent } from 'pages/partner_create_edit/help';
import { PageState } from 'pages/partner_create_edit/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { License, LicenseApi } from 'apis/LicenseApi';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { User, UsersApi } from 'apis/UsersApi';
import { emailStringValidation } from 'utils';
import { Breadcrumb } from 'react-bootstrap';
import { PageTitle } from 'utils/pageTitle';
import { Country } from 'apis/CountryApi';
import { StateApi } from 'apis/StateApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/partner_create_edit/components/partner_create_edit_wizard/styles/partner-create-edit-wizard.scss';

const defaultPartner: PartnerDetails = {
    Active: true,
    Address1: '',
    Address2: '',
    City: '',
    ContactEmail: '',
    Country: { Id: 0, Name: '' },
    CustomerIds: '',
    CustomerIDString: '',
    DateCreate: new Date(),
    DateModified: new Date(),
    DisabledDetailsBtn: false,
    IconURL: '',
    ID: 0,
    Id: 0,
    Language: { Id: 0, Name: '' },
    Name: '',
    OperatingCountries: [],
    PartnerType: PartnerTypes.TestingCenter,
    Phone: '',
    Policy: '',
    Sector: null,
    SectorName: '',
    State: { Id: 0, Name: '' },
    StateID: 0,
    UserEmailList: '',
    Zip: ''
};

export const PartnerCreateEditWizard = props => {

    const { highestRole } = useApp();
    const { pageState, partnerId, partnerType, permissions, setStates, states } = usePartnerCreateEdit();
    const { formatMessage } = useIntl();
    const { catchWithToast, createToast } = useNotification();
    const licensesSectionRef = useRef<LicensesSectionHandle>(null);
    const usersSectionRef = useRef<UsersSectionHandle>(null);
    const [licensesCollapsed, setLicensesCollapsed] = useState(false);
    const [usersCollapsed, setUsersCollapsed] = useState(false);

    const [state, setState] = useSuperState({
        contactEmailIsValid: true,
        createdPartnerId: 0,
        formDetails: { ...defaultPartner, PartnerType: partnerType } as PartnerDetails,
        homeCountryIsValid: true,
        languageIsValid: true,
        licenses: [] as License[],
        licensesLoaded: false,
        licensesLoading: false,
        loading: true,
        openPickerWhenLoaded: false,
        operatingCountries: [] as Country[],
        organizationIsValid: true,
        partnersCurrentLicenses: [] as License[],
        saving: false,
        sectorIsValid: true,
        stagedLicenses: [] as License[],
        stagedUsers: [] as StagedUser[],
        stateIsValid: true,
        userCount: 0,
        users: [] as User[]
    });

    const { createdPartnerId, formDetails, licenses, loading, operatingCountries, partnersCurrentLicenses, saving, stagedLicenses, stagedUsers, users } = state;

    // The effective partner ID: in edit mode it's from props, in create mode it's set after initial save
    const effectivePartnerId = pageState === PageState.Edit ? partnerId : createdPartnerId;
    const partnerSaved = effectivePartnerId > 0;
    const isCreateMode = pageState === PageState.Create;

    // Load data on mount
    useEffect(() => {

        const loadData = async () => {

            try {

                if (pageState === PageState.Edit && partnerId > 0) {

                    // These three don't depend on each other — load them in parallel.
                    // The full orderable-license catalog is NOT loaded here; it can take
                    // a long time for high-level roles and is only needed by the
                    // "Add License" picker, so it loads on first use (see handleAddLicenseClick).
                    const [partner, partnerLicenses, partnerUsers] = await Promise.all([
                        PartnerApi.Get(partnerId),
                        LicenseApi.GetAll(1, partnerId),
                        UsersApi.GetAll(1, partnerId)
                    ]);
                    partner.Id = partner.ID;

                    // These need the partner record first
                    const [opCountries, fetchedStates] = await Promise.all([
                        partner.PartnerType !== PartnerTypes.TestingCenter
                            ? PartnerApi.GetPartnerOperatingCountries(partnerId)
                            : Promise.resolve([] as Country[]),
                        partner.Country?.Id
                            ? StateApi.GetAll(partner.Country.Id)
                            : Promise.resolve(null)
                    ]);
                    if (fetchedStates) {

                        setStates(fetchedStates);

                    }

                    setState({
                        formDetails: partner,
                        loading: false,
                        operatingCountries: opCountries,
                        partnersCurrentLicenses: partnerLicenses,
                        users: partnerUsers
                    });

                } else {

                    setState({
                        formDetails: { ...defaultPartner, PartnerType: partnerType },
                        loading: false
                    });

                }

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_load_partner_data.079a3045', defaultMessage: 'Failed to load partner data.' }))(e);
                setState({ loading: false });

            }

        };
        loadData();

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Lazily load the orderable-license catalog the first time the picker is needed.
    // For high-level roles this is the whole system's catalog and is by far the most
    // expensive call on this page, so it must never gate the initial render.
    const handleAddLicenseClick = async () => {

        if (state.licensesLoaded) {

            licensesSectionRef.current?.openAddDialog();
            return;

        }

        try {

            setState({ licensesLoading: true });
            const allLicenses = await LicenseApi.GetAll(1, 0, highestRole.partnerId);

            // Filter out licenses the partner already has (or has staged, in create mode)
            const currentLicenses = isCreateMode ? state.stagedLicenses : state.partnersCurrentLicenses;
            setState({
                licenses: allLicenses.filter(l => !currentLicenses.find(pl => pl.ID === l.ID)),
                licensesLoaded: true,
                licensesLoading: false,
                openPickerWhenLoaded: true
            });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_load_partner_data.079a3045', defaultMessage: 'Failed to load partner data.' }))(e);
            setState({ licensesLoading: false });

        }

    };

    // The picker dialog only mounts once the catalog is in state, so open it from an
    // effect after the render that follows the lazy load rather than from the handler.
    useEffect(() => {

        if (state.openPickerWhenLoaded && state.licensesLoaded) {

            setState({ openPickerWhenLoaded: false });
            if (state.licenses.length > 0) {

                licensesSectionRef.current?.openAddDialog();

            }

        }

    }, [state.openPickerWhenLoaded, state.licensesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    const setFormDetails = (partial: Partial<PartnerDetails>) => {

        setState({ formDetails: { ...state.formDetails, ...partial } });

    };

    // ── Staged mode callbacks (create mode only) ──

    const onStageAddLicenses = (addedLicenses: License[]) => {

        setState({
            licenses: state.licenses.filter(l => !addedLicenses.find(a => a.ID === l.ID)),
            stagedLicenses: [...state.stagedLicenses, ...addedLicenses]
        });

    };

    const onStageRemoveLicense = (license: License) => {

        setState({
            licenses: [...state.licenses, license],
            stagedLicenses: state.stagedLicenses.filter(l => l.ID !== license.ID)
        });

    };

    const onStageUser = (staged: StagedUser) => {

        setState({ stagedUsers: [...state.stagedUsers, staged] });

    };

    const onRemoveStagedUser = (index: number) => {

        setState({ stagedUsers: state.stagedUsers.filter((_, i) => i !== index) });

    };

    const validatePartnerDetails = (details?: PartnerDetails): boolean => {

        const d = details ?? formDetails;
        const sectorValid = d.Sector !== null;
        const languageValid = d.Language?.Id !== 0;
        const organizationValid = d.Name.length > 0;
        const emailValid = d.ContactEmail.length > 0 && emailStringValidation(d.ContactEmail);
        const homeCountryValid = d.Country?.Id !== 0;
        const stateValid = states.length > 1 ? d.State?.Id !== 0 : true;

        const errors: string[] = [];
        if (!organizationValid) errors.push(formatMessage({ id: 'organization_is_required.bc24b3d1', defaultMessage: 'Organization is required' }));
        if (!emailValid) {

            errors.push(d.ContactEmail.length === 0
                ? formatMessage({ id: 'email_is_required.d188dd34', defaultMessage: 'Email is required' })
                : formatMessage({ id: 'email_format_is_invalid.f6b37aa1', defaultMessage: 'Email format is invalid', description: 'Validadores' }));

        }
        if (!sectorValid) errors.push(formatMessage({ id: 'sector_is_required.461eda94', defaultMessage: 'Sector is required' }));
        if (!languageValid) errors.push(formatMessage({ id: 'language_is_required.e4e4fde8', defaultMessage: 'Language is required' }));
        if (!homeCountryValid) errors.push(formatMessage({ id: 'country_is_required.e13df403', defaultMessage: 'Country is required' }));
        if (!stateValid) errors.push(formatMessage({ id: 'state_is_required.22c36766', defaultMessage: 'State is required' }));

        setState({
            contactEmailIsValid: emailValid,
            homeCountryIsValid: homeCountryValid,
            languageIsValid: languageValid,
            organizationIsValid: organizationValid,
            sectorIsValid: sectorValid,
            stateIsValid: stateValid
        });

        if (errors.length > 0) {

            createToast(errors.join('. ') + '.', 'danger');
            return false;

        }
        return true;

    };

    const handleSaveDetails = async (detailsOverride?: PartnerDetails, countriesOverride?: Country[]): Promise<boolean> => {

        const detailsToSave = detailsOverride ?? formDetails;
        const countriesToSave = countriesOverride ?? operatingCountries;

        if (!validatePartnerDetails(detailsOverride)) return false;

        setState({ saving: true });

        try {

            if (pageState === PageState.Edit) {

                const partnerToUpdate = {
                    ...detailsToSave,
                    OperatingCountries: countriesToSave
                };
                const result = await PartnerApi.Update(partnerToUpdate);
                if (result.Done) {

                    createToast(formatMessage({ id: 'your_information_has_been_saved_successf.f7641f9c', defaultMessage: 'Your information has been saved successfully.', description: 'Mensaje' }), 'success');
                    return true;

                } else {

                    createToast(result.Message || formatMessage({ id: 'failed_to_update.419f8ec3', defaultMessage: 'Failed to update' }), 'danger');
                    return false;

                }

            } else {

                // Create mode — validate all three sections
                const errors: string[] = [];
                if (stagedLicenses.length === 0) errors.push(formatMessage({ id: 'at_least_one_license_is_required.78b8fec5', defaultMessage: 'At least one license is required' }) || 'At least one license is required');
                if (stagedUsers.length === 0) errors.push(formatMessage({ id: 'at_least_one_user_is_required.17036ccb', defaultMessage: 'At least one user is required' }) || 'At least one user is required');

                if (errors.length > 0) {

                    createToast(errors.join(' '), 'danger');
                    setState({ saving: false });
                    return false;

                }

                // Show loader until page refresh completes
                setState({ loading: true });

                // 1. Create the partner
                const partnerToCreate = {
                    ...detailsToSave,
                    OperatingCountries: countriesToSave
                };
                const result = await PartnerApi.Create(partnerToCreate);
                if (!result.Done || !result.Id) {

                    createToast(result.Message || formatMessage({ id: 'failed_to_create_partner.73437231', defaultMessage: 'Failed to create partner.' }), 'danger');
                    setState({ loading: false, saving: false });
                    return false;

                }

                const newPartnerId = result.Id;

                // 2. Add staged licenses
                await PartnerApi.AddLicensesToPartner(
                    newPartnerId,
                    stagedLicenses.map(l => l.Id),
                    stagedLicenses.map(l => l.SuppliedBy)
                );

                // 3. Create staged users
                for (const staged of stagedUsers) {

                    if (staged.existingUser) {

                        await UsersApi.Update(staged.user, staged.permissionsCSV, newPartnerId);

                    } else {

                        await UsersApi.Create(staged.user, staged.permissionsCSV, newPartnerId, partnerType, staged.allowUserToSetPassword);

                    }

                }

                // 4. Update session so the server knows the selected partner
                await PartnerApi.SetSelectedPartner(newPartnerId);

                // 5. Update URL and refresh so the master page reflects the new partner
                const params = new URLSearchParams(window.location.search);
                params.set('PageState', 'Edit');
                window.location.search = params.toString();
                return true;

            }

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_save_partner.b27fb348', defaultMessage: 'Failed to save partner.' }))(e);
            setState({ loading: false });
            return false;

        } finally {

            setState({ saving: false });

        }

    };

    if (loading) {

        return (
            <div className={'d-flex align-items-center justify-content-center h-100'}>
                <GMetrixLoader />
            </div>
        );

    }

    const partnerTypeName = (() => {

        switch (partnerType) {

            case PartnerTypes.Distributor: return formatMessage({ id: 'distributor.8955b2a4', defaultMessage: 'Distributor' });
            case PartnerTypes.Reseller: return formatMessage({ id: 'reseller.8d01b60c', defaultMessage: 'Reseller' });
            case PartnerTypes.TestingCenter: return formatMessage({ id: 'testing_center.fda9aa47', defaultMessage: 'Testing Center' });
            default: return formatMessage({ id: 'partners.84d264f4', defaultMessage: 'Partners' });

        }

    })();

    const partnersListHref = `/partners?PType=${partnerType}`;

    const handleBackToPartners = async (e: React.MouseEvent) => {

        e.preventDefault();
        try {

            await PartnerApi.ClearSelectedPartner();

        } finally {

            // Full-page navigation so all server-side state (selected partner, side-nav, etc.) refreshes
            window.location.href = partnersListHref;

        }

    };

    const pageTitle = isCreateMode
        ? `${formatMessage({ id: 'create.be56b47d', defaultMessage: 'Create' })} ${partnerTypeName}`
        : `${formatMessage({ id: 'partner_details.a729f457', defaultMessage: 'Partner Details' })} — ${formDetails.Name}`;

    return (
        <>
            <PageTitle title={`${pageTitle} | GMetrix`} />
            {/* ── Page header ── */}
            {!isCreateMode && (
                <Breadcrumb>
                    <Breadcrumb.Item href={partnersListHref} onClick={handleBackToPartners}>
                        {<FormattedMessage defaultMessage="Partners" id="partners.84d264f4" />}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>
                        {formDetails.Name}
                    </Breadcrumb.Item>
                </Breadcrumb>
            )}

            <PageHeader
                cta={isCreateMode ? {
                    disabled: saving,
                    label: formatMessage({ id: 'create.be56b47d', defaultMessage: 'Create' }),
                    onClick: () => handleSaveDetails()
                } : undefined}
                helpContent={helpContent}
                title={pageTitle}
            />

            {/* ── Partner Details Card ── */}
            <div className="card mb-3">
                <div className="card-body">
                    <PartnerDetailsSection
                        validation={{
                            contactEmailIsValid: state.contactEmailIsValid,
                            homeCountryIsValid: state.homeCountryIsValid,
                            languageIsValid: state.languageIsValid,
                            organizationIsValid: state.organizationIsValid,
                            sectorIsValid: state.sectorIsValid,
                            stateIsValid: state.stateIsValid
                        }}
                        formDetails={formDetails}
                        licenseCount={isCreateMode ? stagedLicenses.length : partnersCurrentLicenses.length}
                        onSave={handleSaveDetails}
                        operatingCountries={partnerType !== PartnerTypes.TestingCenter ? operatingCountries : undefined}
                        readOnly={partnerSaved && !isCreateMode}
                        saving={saving}
                        setFormDetails={setFormDetails}
                        setOperatingCountries={partnerType !== PartnerTypes.TestingCenter ? val => setState({ operatingCountries: val }) : undefined}
                        userCount={isCreateMode ? stagedUsers.length : users.length}
                    />
                </div>
            </div>

            {/* ── Licenses Card ── */}
            <div className="card mb-3">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="partner-wizard__section-heading mb-0" onClick={() => setLicensesCollapsed(!licensesCollapsed)} role="button">
                            <i className={`partner-wizard__section-chevron fa-solid fa-chevron-${licensesCollapsed ? 'right' : 'down'} me-2`} />
                            <FormattedMessage defaultMessage="Licenses" id="licenses.11b1c36a" />
                        </h4>
                        {(partnerSaved || isCreateMode) && !permissions.IsAssociation && !licensesCollapsed && (
                            <GMetrixButton
                                disabled={state.licensesLoading || (state.licensesLoaded && licenses.length === 0)}
                                iconClass={state.licensesLoading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-plus'}
                                onClick={handleAddLicenseClick}
                                type="button"
                                variant="primary"
                            >
                                {<FormattedMessage defaultMessage="Add License" id="add_license.e6199c74" /> || 'Add License'}
                            </GMetrixButton>
                        )}
                    </div>
                    {!licensesCollapsed && (
                        <>
                            <hr />
                            {(partnerSaved || isCreateMode) ? (
                                <LicensesSection
                                    licenses={licenses}
                                    onStageAdd={isCreateMode ? onStageAddLicenses : undefined}
                                    onStageRemove={isCreateMode ? onStageRemoveLicense : undefined}
                                    partnerId={effectivePartnerId}
                                    partnersCurrentLicenses={isCreateMode ? stagedLicenses : partnersCurrentLicenses}
                                    ref={licensesSectionRef}
                                    searchAutoFocus={false}
                                    setLicenses={val => setState({ licenses: val })}
                                    setPartnersCurrentLicenses={isCreateMode ? val => setState({ stagedLicenses: val }) : val => setState({ partnersCurrentLicenses: val })}
                                    stagedMode={isCreateMode}
                                />
                            ) : (
                                <p className="text-muted mb-0">
                                    <FormattedMessage defaultMessage="Save partner details first to manage licenses." id="save_partner_details_first_to_manage_lic.428143f5" />
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ── Users Card ── */}
            <div className="card mb-3">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="partner-wizard__section-heading mb-0" onClick={() => setUsersCollapsed(!usersCollapsed)} role="button">
                            <i className={`partner-wizard__section-chevron fa-solid fa-chevron-${usersCollapsed ? 'right' : 'down'} me-2`} />
                            <FormattedMessage defaultMessage="Users" id="users.581e673f" />
                        </h4>
                        {(partnerSaved || isCreateMode) && !usersCollapsed && (
                            <GMetrixButton
                                iconClass="fa-solid fa-plus"
                                onClick={() => usersSectionRef.current?.openAddDialog()}
                                type="button"
                                variant="primary"
                            >
                                {<FormattedMessage defaultMessage="Add User" id="add_user.daaf2592" /> || 'Add User'}
                            </GMetrixButton>
                        )}
                    </div>
                    {!usersCollapsed && (
                        <>
                            <hr />
                            {(partnerSaved || isCreateMode) ? (
                                <UsersSection
                                    onRemoveStagedUser={isCreateMode ? onRemoveStagedUser : undefined}
                                    onStageUser={isCreateMode ? onStageUser : undefined}
                                    onUserCountChange={count => setState({ userCount: count })}
                                    partnerId={effectivePartnerId}
                                    partnerType={partnerType}
                                    ref={usersSectionRef}
                                    searchAutoFocus={false}
                                    stagedMode={isCreateMode}
                                    stagedUsers={isCreateMode ? stagedUsers : undefined}
                                />
                            ) : (
                                <p className="text-muted mb-0">
                                    <FormattedMessage defaultMessage="Save partner details first to manage users." id="save_partner_details_first_to_manage_use.bbf54dd1" />
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );

};
